import React from 'react';
import { Globe2, Shield, Target, SlidersHorizontal, ChevronDown, ChevronUp, X, Heart } from 'lucide-react';
import { budgetCountries } from '../../data/budget-countries';

interface MatchingFiltersProps {
  onFilterChange: (filters: { destination?: string, pvtStatus?: string, objective?: string, likedOnly?: boolean }) => void;
  currentUserId?: string;
}

const PVT_OBJECTIVES = [
  { id: 'language', label: 'Ameliorer une langue' },
  { id: 'career', label: 'Faire evoluer sa carriere' },
  { id: 'travel', label: 'Voyager et explorer' },
  { id: 'nature', label: 'Experience en pleine nature' },
  { id: 'culture', label: 'Decouvrir la culture locale & faire la fete' }
];

export function MatchingFilters({ onFilterChange, currentUserId }: MatchingFiltersProps) {
  const [selectedDestination, setSelectedDestination] = React.useState<string>('');
  const [selectedPvtStatus, setSelectedPvtStatus] = React.useState<string>('');
  const [selectedObjective, setSelectedObjective] = React.useState<string>('');
  const [showLikedOnly, setShowLikedOnly] = React.useState<boolean>(false);
  const [isExpanded, setIsExpanded] = React.useState(false);

  const activeFilterCount = [selectedDestination, selectedPvtStatus, selectedObjective, showLikedOnly].filter(Boolean).length;

  const buildFilters = (overrides: Partial<{ destination: string; pvtStatus: string; objective: string; likedOnly: boolean }> = {}) => ({
    destination: overrides.destination ?? (selectedDestination || undefined),
    pvtStatus: overrides.pvtStatus ?? (selectedPvtStatus || undefined),
    objective: overrides.objective ?? (selectedObjective || undefined),
    likedOnly: overrides.likedOnly ?? (showLikedOnly || undefined),
  });

  const showToast = (message: string) => {
    const toast = document.createElement('div');
    toast.className = 'fixed bottom-4 left-1/2 -translate-x-1/2 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 px-5 py-2.5 rounded-xl shadow-lg border border-amber-100 dark:border-amber-800/30 z-50 flex items-center gap-2 animate-fade-in text-sm';
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  };

  const handleDestinationChange = (destination: string) => {
    setSelectedDestination(destination);
    if (!destination) {
      setSelectedPvtStatus('');
      setSelectedObjective('');
    }
    onFilterChange(buildFilters({
      destination: destination || undefined,
      pvtStatus: !destination ? undefined : selectedPvtStatus || undefined,
      objective: !destination ? undefined : selectedObjective || undefined,
    }));
  };

  const handlePvtStatusChange = (status: string) => {
    if (!selectedDestination && status) {
      showToast('Veuillez d\'abord selectionner une destination');
      return;
    }
    setSelectedPvtStatus(status);
    onFilterChange(buildFilters({ pvtStatus: status || undefined }));
  };

  const handleObjectiveChange = (objective: string) => {
    if (!selectedDestination && objective) {
      showToast('Veuillez d\'abord selectionner une destination');
      return;
    }
    setSelectedObjective(objective);
    onFilterChange(buildFilters({ objective: objective || undefined }));
  };

  const handleLikedOnlyToggle = (value: boolean) => {
    setShowLikedOnly(value);
    onFilterChange(buildFilters({ likedOnly: value || undefined }));
  };

  const resetFilters = () => {
    setSelectedDestination('');
    setSelectedPvtStatus('');
    setSelectedObjective('');
    setShowLikedOnly(false);
    onFilterChange({});
  };

  const activeSummary = [
    selectedDestination && budgetCountries.find(c => c.code === selectedDestination)?.name,
    selectedPvtStatus && (selectedPvtStatus === 'obtained' ? 'PVT obtenu' : selectedPvtStatus === 'pending' ? 'En cours' : 'Pas encore demande'),
    selectedObjective && PVT_OBJECTIVES.find(o => o.id === selectedObjective)?.label,
    showLikedOnly && 'Profils likes',
  ].filter(Boolean);

  return (
    <div className="w-full">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-200 ${
          isExpanded
            ? 'bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-700 rounded-b-none'
            : 'bg-white dark:bg-gray-800 shadow-sm border border-gray-200/80 dark:border-gray-700/50 hover:border-gray-300 dark:hover:border-gray-600'
        }`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={`p-1.5 rounded-lg transition-colors ${activeFilterCount > 0 ? 'bg-primary-50 dark:bg-primary-900/20' : 'bg-gray-50 dark:bg-gray-700/50'}`}>
            <SlidersHorizontal className={`w-4 h-4 ${activeFilterCount > 0 ? 'text-primary-500' : 'text-gray-400 dark:text-gray-500'}`} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {activeFilterCount > 0
                ? `${activeFilterCount} filtre${activeFilterCount > 1 ? 's' : ''}`
                : 'Filtrer'}
            </p>
            {activeSummary.length > 0 && (
              <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                {activeSummary.join(' / ')}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {activeFilterCount > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 bg-primary-500 text-white text-[10px] font-bold rounded-full">
              {activeFilterCount}
            </span>
          )}
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </button>

      <div
        className={`overflow-hidden transition-all duration-300 ${
          isExpanded
            ? 'max-h-[500px] opacity-100'
            : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-white dark:bg-gray-800 border border-t-0 border-gray-200 dark:border-gray-700 rounded-b-xl shadow-md p-4 sm:p-5">
          {activeFilterCount > 0 && (
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100 dark:border-gray-700/50">
              <div className="flex flex-wrap gap-1.5">
                {selectedDestination && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-md text-xs font-medium text-gray-600 dark:text-gray-300">
                    <Globe2 className="w-3 h-3" />
                    {budgetCountries.find(c => c.code === selectedDestination)?.name}
                    <button onClick={() => handleDestinationChange('')} className="ml-0.5 hover:text-primary-500 transition-colors">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {selectedPvtStatus && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-md text-xs font-medium text-gray-600 dark:text-gray-300">
                    <Shield className="w-3 h-3" />
                    {selectedPvtStatus === 'obtained' ? 'Obtenu' : selectedPvtStatus === 'pending' ? 'En cours' : 'Non demande'}
                    <button onClick={() => handlePvtStatusChange('')} className="ml-0.5 hover:text-primary-500 transition-colors">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {selectedObjective && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-md text-xs font-medium text-gray-600 dark:text-gray-300">
                    <Target className="w-3 h-3" />
                    {PVT_OBJECTIVES.find(o => o.id === selectedObjective)?.label}
                    <button onClick={() => handleObjectiveChange('')} className="ml-0.5 hover:text-primary-500 transition-colors">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {showLikedOnly && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-md text-xs font-medium text-gray-600 dark:text-gray-300">
                    <Heart className="w-3 h-3" />
                    Likes
                    <button onClick={() => handleLikedOnlyToggle(false)} className="ml-0.5 hover:text-primary-500 transition-colors">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
              </div>
              <button
                onClick={resetFilters}
                className="text-xs font-medium text-gray-400 hover:text-primary-500 transition-colors flex-shrink-0 ml-2"
              >
                Tout effacer
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Destination</label>
              <select
                value={selectedDestination}
                onChange={(e) => handleDestinationChange(e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-colors"
              >
                <option value="">Toutes</option>
                {budgetCountries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.flag} {country.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={`block text-xs font-medium mb-1.5 transition-colors ${!selectedDestination ? 'text-gray-300 dark:text-gray-600' : 'text-gray-500 dark:text-gray-400'}`}>
                Statut PVT
              </label>
              <select
                value={selectedPvtStatus}
                onChange={(e) => handlePvtStatusChange(e.target.value)}
                disabled={!selectedDestination}
                className={`w-full px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-colors ${
                  !selectedDestination ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <option value="">Tous</option>
                <option value="obtained">Obtenu</option>
                <option value="pending">En cours</option>
                <option value="not-requested">Non demande</option>
              </select>
            </div>

            <div>
              <label className={`block text-xs font-medium mb-1.5 transition-colors ${!selectedDestination ? 'text-gray-300 dark:text-gray-600' : 'text-gray-500 dark:text-gray-400'}`}>
                Objectif
              </label>
              <select
                value={selectedObjective}
                onChange={(e) => handleObjectiveChange(e.target.value)}
                disabled={!selectedDestination}
                className={`w-full px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-colors ${
                  !selectedDestination ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <option value="">Tous</option>
                {PVT_OBJECTIVES.map((obj) => (
                  <option key={obj.id} value={obj.id}>{obj.label}</option>
                ))}
              </select>
            </div>
          </div>

          {currentUserId && (
            <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700/50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Profils likes uniquement</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Affiche uniquement vos favoris</p>
                </div>
                <button
                  onClick={() => handleLikedOnlyToggle(!showLikedOnly)}
                  role="switch"
                  aria-checked={showLikedOnly}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                    showLikedOnly ? 'bg-primary-500' : 'bg-gray-200 dark:bg-gray-600'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
                    showLikedOnly ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
